import React, { useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import useParameterStore from "../../store/parameterStore";
import LayerValueSelect from "../components/LayerValueSelect";
import useToolLayers from "../hooks/useToolLayers";

const TYPE_OPTIONS = [
  { label: "I", value: "I" },
  { label: "F", value: "F" },
  { label: "B", value: "B" },
  { label: "S", value: "S" },
  { label: "LPP", value: "LPP" },
];

const createEmptyRow = () => ({
  id: `param_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  name: "",
  type: "I",
  defaultValue: "",
  groupName: "",
  ranges: [],
});

const normalizeRow = (row = {}) => ({
  id: row.id || `param_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  name: row.name || "",
  type: row.type || row.datatype || "I",
  defaultValue: row.defaultValue ?? row.defaultVal ?? row.value ?? "",
  groupName: row.groupName || "",
  ranges: Array.isArray(row.ranges) ? row.ranges : [],
});

const getDefaultValueError = (type, value) => {
  if (type === "LPP") {
    if (value === undefined || value === null || value === "") return "";
    if (
      typeof value !== "object" ||
      !String(value.layer || "").trim() ||
      !String(value.datatype || "").trim()
    ) {
      return "Select a valid layer and datatype from the layer dropdown";
    }
    return "";
  }

  const val = String(value ?? "").trim();
  if (!val) return "";

  if (type === "I" && !/^-?\d+$/.test(val)) {
    return "Only whole integer values are allowed, for example 10, 20, 30 or 40";
  }
  if (type === "F" && !/^-?(?:\d+\.\d+|\.\d+)$/.test(val)) {
    return "Only decimal values are allowed, for example 5.0, 10.5 or -2.5";
  }
  if (type === "B" && !/^(0|1|T|nil)$/i.test(val)) {
    return "Only binary values 0, 1, T or nil are allowed";
  }
  if (type === "S" && !/^[A-Za-z0-9_]+$/.test(val)) {
    return "Only letters, numbers and underscore are allowed for string values";
  }

  return "";
};

const getDuplicateNames = (currentRows, otherRows = []) => {
  const seen = new Set();
  const duplicates = new Set();

  for (const row of [...currentRows, ...otherRows]) {
    const name = String(row.name || "").trim().toLowerCase();
    if (!name) continue;

    if (seen.has(name)) duplicates.add(name);
    seen.add(name);
  }

  return duplicates;
};

const GlobalTabContent = () => {
  const [selectedRowId, setSelectedRowId] = useState(null);
  const rows = useParameterStore((state) => state.globalParameters);
  const localRows = useParameterStore((state) => state.localParameters);
  const setRows = useParameterStore((state) => state.setGlobalParameters);
  const toolLayers = useToolLayers();

  useEffect(() => {
    if (!rows.length) setRows([createEmptyRow()]);
  }, [rows.length, setRows]);

  const duplicateNames = getDuplicateNames(rows, localRows);

  const updateRows = (nextRows) => {
    setRows(nextRows.map(normalizeRow));
  };

  const addRow = () => {
    updateRows([...rows, createEmptyRow()]);
  };

  const removeRow = () => {
    if (!selectedRowId) return;

    const nextRows = rows.filter((row) => row.id !== selectedRowId);
    updateRows(nextRows.length ? nextRows : [createEmptyRow()]);
    setSelectedRowId(null);
  };

  const updateRow = (index, patch) => {
    const nextRows = rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...patch } : row
    );

    updateRows(nextRows);
  };

  return (
    <>
      <div style={headerStyle}>
        <span style={labelStyle}>Global</span>

        <div style={headerRightStyle}>
  <button type="button" style={iconBtn} onClick={removeRow}>
    <DeleteIcon style={muiIconStyle} />
  </button>

  <button type="button" style={iconBtn} onClick={addRow}>
    <AddIcon style={muiIconStyle} />
  </button>
</div>
      </div>

      <div style={columnLabelRow}>
        <div style={{ width: "88px" }}>Name</div>
        <div style={{ width: "62px" }}>Type</div>
        <div style={{ width: "82px" }}>Default</div>
        <div style={{ flex: 1 }}>GroupName</div>
      </div>

      {rows.map((row, index) => {
        const defaultError = getDefaultValueError(row.type, row.defaultValue);
        const isDuplicate =
          duplicateNames.has(String(row.name || "").trim().toLowerCase());

        return (
          <div
            key={row.id}
            style={rowStyle}
            onFocus={() => setSelectedRowId(row.id)}
          >
            <input
              value={row.name || ""}
              onChange={(e) => updateRow(index, { name: e.target.value })}
              placeholder="cols"
              title={isDuplicate ? "Parameter name must be unique across Global and Local parameters" : ""}
style={{
  ...inputStyle,
  width: "88px",
  border: isDuplicate ? "1px solid #ff3b3b" : "1px solid #444",
}}
            />

            <select
              value={row.type || "I"}
              onChange={(e) => {
                const nextType = e.target.value;
                updateRow(index, {
                  type: nextType,
                  ...(nextType === "LPP" || row.type === "LPP"
                    ? { defaultValue: "" }
                    : {}),
                });
              }}
              style={{ ...inputStyle, width: "62px" }}
            >
              {TYPE_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            {row.type === "LPP" ? (
              <div title={defaultError} style={{ width: "82px", minWidth: 0 }}>
                <LayerValueSelect
                  layers={toolLayers}
                  value={row.defaultValue}
                  onChange={(value) =>
                    updateRow(index, { defaultValue: value })
                  }
                  height={30}
                  hasError={Boolean(defaultError)}
                  compactSelectedValue
                />
              </div>
            ) : (
              <input
                value={row.defaultValue ?? ""}
                onChange={(e) =>
                  updateRow(index, { defaultValue: e.target.value })
                }
                placeholder="2"
                title={defaultError}
                style={{
                  ...inputStyle,
                  width: "82px",
                  border: defaultError ? "1px solid #ff3b3b" : "1px solid #444",
                }}
              />
            )}

            <input
              value={row.groupName || ""}
              onChange={(e) => updateRow(index, { groupName: e.target.value })}
              placeholder="default"
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
        );
      })}
    </>
  );
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "6px",
};

const headerRightStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const labelStyle = {
  fontWeight: "bold",
  color: "#00bcd4",
  fontSize: "14px",
};

const iconBtn = {
  backgroundColor: "#2c2c2c",
  border: "1px solid #444",
  borderRadius: "5px",
  padding: "2px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
};

const muiIconStyle = {
  fontSize: "12px",
  color: "white",
};

const columnLabelRow = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  marginBottom: "6px",
  fontSize: "12px",
  color: "#aaa",
  marginLeft: "4px",
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  marginBottom: "6px",
};

const inputStyle = {
  backgroundColor: "#2a2a2a",
  color: "#fff",
  border: "1px solid #444",
  borderRadius: "3px",
  padding: "6px",
  fontSize: "12px",
  height: "30px",
  boxSizing: "border-box",
};

export default GlobalTabContent;
